import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { families, getProductsByFamily } from "../data/products";
import useSEO from "../hooks/useSEO";

function ProductRow({ p, idx }) {
  const flipped = idx % 2 === 1;
  return (
    <article
      className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center"
      data-testid={`gamme-row-${p.slug}`}
    >
      <div className={`lg:col-span-7 ${flipped ? "lg:order-2" : ""}`}>
        <div className="relative aspect-[4/3] product-card-img overflow-hidden">
          {p.hero || p.cardImage ? (
            <img
              src={p.hero || p.cardImage}
              alt={p.name}
              className={`w-full h-full ${
                p.cardFit === "contain" ? "object-contain" : "object-cover"
              }`}
            />
          ) : (
            <div className="absolute inset-0 placeholder-bois flex flex-col items-center justify-center">
              <span className="font-mono text-[11px] uppercase tracking-widest text-[var(--yono-warm-grey)] mb-2">
                Visuel à venir
              </span>
              <span className="font-heading text-3xl">{p.name}</span>
            </div>
          )}
        </div>
      </div>

      <div className={`lg:col-span-5 ${flipped ? "lg:order-1" : ""}`}>
        <div className="eyebrow text-[var(--yono-wood)] mb-3">
          YONO · {p.surface} · {p.capacity}
        </div>
        <h2 className="font-heading text-[26px] sm:text-[30px] lg:text-[36px] tracking-tight leading-[1.1]">
          {p.name}
        </h2>
        <p className="mt-3 text-[var(--yono-warm-grey)] text-[15px]">
          {p.tagline}
        </p>
        <p className="mt-5 text-[15px] leading-[1.85] text-[var(--yono-soft-black)]/85">
          {p.intro}
        </p>

        <div className="mt-5 flex flex-wrap gap-x-5 gap-y-1">
          {p.chips.map((c) => (
            <span
              key={c}
              className="text-[11px] tracking-[0.15em] uppercase text-[var(--yono-warm-grey)]"
            >
              · {c}
            </span>
          ))}
        </div>

        <ul className="mt-5 space-y-2">
          {p.usages.slice(0, 5).map((u) => (
            <li
              key={u}
              className="flex items-start gap-3 text-[14px] text-[var(--yono-soft-black)]/85"
            >
              <Check
                size={14}
                strokeWidth={1.5}
                className="mt-1 shrink-0 text-[var(--yono-wood)]"
              />
              {u}
            </li>
          ))}
        </ul>

        <div className="mt-7 flex gap-3 flex-wrap">
          <Link
            to={`/produits/${p.slug}`}
            className="btn-primary"
            data-testid={`gamme-cta-${p.slug}`}
          >
            Voir la fiche produit{" "}
            <ArrowRight size={13} strokeWidth={1.5} />
          </Link>
          <Link to="/contact" className="btn-outline">
            Étudier un projet
          </Link>
        </div>
        <p className="mt-4 text-[15px] leading-[1.5] text-[var(--yono-soft-black)]">
          <span className="font-medium">Prix sur devis</span>
          <span className="block text-[13px] text-[var(--yono-warm-grey)] mt-0.5">
            Selon configuration, équipements et livraison.
          </span>
        </p>
      </div>
    </article>
  );
}

function FamilySection({ family, startIdx }) {
  const items = getProductsByFamily(family.slug);
  return (
    <div data-testid={`family-section-${family.slug}`}>
      <div className="border-t border-[var(--yono-border)] pt-7 mb-10 lg:mb-14">
        <div className="flex items-baseline justify-between gap-6">
          <p className="eyebrow text-[var(--yono-wood)]">
            Famille {family.index}
          </p>
          <span className="hidden sm:inline text-[12px] text-[var(--yono-warm-grey)] tracking-[0.12em] uppercase">
            {family.name}
          </span>
        </div>
        <h2 className="mt-4 font-heading text-[28px] sm:text-[34px] lg:text-[40px] tracking-tight leading-[1.1]">
          {family.name}
        </h2>
        <p className="mt-5 text-[15px] leading-[1.85] text-[var(--yono-soft-black)]/80 max-w-3xl">
          {family.intro}
        </p>
      </div>

      <div className="space-y-20 lg:space-y-24">
        {items.map((p, i) => (
          <ProductRow key={p.slug} p={p} idx={startIdx + i} />
        ))}
      </div>
    </div>
  );
}

export default function Gamme() {
  useSEO({
    title: "Habitats bois — 3 familles, 6 modèles",
    description:
      "Découvrez l'offre YONO organisée en trois familles : Micro-habitats (Micro, Studio, Nido), Lodges (Lodge T3, Lodge T4) et Hébergements saisonniers (Twin). Habitats bois CLT pour campings, PRL et domaines nature.",
    path: "/produits",
    keywords:
      "habitats bois, YONO Micro, YONO Studio, YONO Nido, YONO Lodge T3, YONO Lodge T4, YONO Twin, micro-habitats, lodges bois, hébergement saisonnier, alternative mobil-home",
  });

  const [activeFamily, setActiveFamily] = useState("all");

  const sections = useMemo(() => {
    const filtered =
      activeFamily === "all"
        ? families
        : families.filter((f) => f.slug === activeFamily);
    let cursor = 0;
    return filtered.map((f) => {
      const startIdx = cursor;
      cursor += getProductsByFamily(f.slug).length;
      return { f, startIdx };
    });
  }, [activeFamily]);

  const filterPills = [
    { slug: "all", label: "Tous" },
    ...families.map((f) => ({ slug: f.slug, label: f.name })),
  ];

  return (
    <div data-testid="gamme-page">
      <section className="pt-12 pb-8">
        <div className="container-yono">
          <div className="eyebrow mb-6">L'offre YONO</div>
          <h1 className="font-heading text-[24px] sm:text-[34px] lg:text-[44px] tracking-tight leading-[1.05] max-w-4xl">
            Nos habitats bois
          </h1>
          <p className="mt-6 text-[15px] leading-[1.85] text-[var(--yono-soft-black)]/80 max-w-3xl">
            Six habitats conçus avec la même exigence de qualité, de confort et
            de durabilité, structurés en trois familles pour répondre aux
            principaux besoins des exploitants : micro-habitats à forte
            rentabilité, lodges compacts en alternative au mobil-home, et
            hébergements dédiés au logement des saisonniers.
          </p>
        </div>
      </section>

      {/* Filter pills */}
      <div className="sticky top-[112px] z-30 bg-[var(--yono-off-white)]/95 backdrop-blur-sm border-y border-[var(--yono-border)]">
        <div className="container-yono py-4 lg:py-5">
          <div
            className="flex flex-wrap gap-2 lg:gap-3"
            role="tablist"
            aria-label="Filtrer par famille"
            data-testid="family-filter"
          >
            {filterPills.map((p) => {
              const isActive = activeFamily === p.slug;
              return (
                <button
                  key={p.slug}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveFamily(p.slug)}
                  data-testid={`filter-pill-${p.slug}`}
                  className={`px-4 lg:px-5 py-2 text-[11px] tracking-[0.15em] uppercase border transition-colors ${
                    isActive
                      ? "bg-[var(--yono-soft-black)] text-[var(--yono-off-white)] border-[var(--yono-soft-black)]"
                      : "bg-transparent text-[var(--yono-soft-black)] border-[var(--yono-border)] hover:border-[var(--yono-soft-black)]"
                  }`}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <section className="py-10 lg:py-14">
        <div className="container-yono space-y-20 lg:space-y-28">
          {sections.map(({ f, startIdx }) => (
            <FamilySection key={f.slug} family={f} startIdx={startIdx} />
          ))}
        </div>
      </section>

      <section className="bg-[var(--yono-beige)] py-16">
        <div className="container-yono text-center">
          <h2 className="font-heading text-[26px] sm:text-[30px] lg:text-[36px] tracking-tight">
            Besoin d'aide pour choisir ?
          </h2>
          <p className="mt-4 text-[var(--yono-warm-grey)] max-w-2xl mx-auto text-[15px] leading-relaxed">
            Notre équipe vous accompagne pour identifier le logement le plus
            adapté à votre projet, vos emplacements et votre stratégie
            d'exploitation.
          </p>
          <Link to="/contact" className="btn-primary mt-7" data-testid="aide-contact-btn">
            Nous contacter <ArrowRight size={14} strokeWidth={1.5} />
          </Link>
        </div>
      </section>
      <div className="bg-[var(--yono-off-white)] h-16" aria-hidden="true" />
    </div>
  );
}
